import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  IconButton,
  Link,
  Stack,
  Typography,
  alpha,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tooltip,
  Menu,
} from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';

import { FieldToExportDialog, Iconify, SearchBar, SearchBarProps } from '@app/components';
import { SvgColor } from '@app/components/svg-color';
import { useDialog } from '@app/hooks';
import { Strings } from '@app/constants';
import { StyledRowSpace } from './styles';

interface Props {
  data?: any;
  csvHead?: any;
  csvFileName?: string;
  goBack?: VoidFunction;
  title?: string;
  helperText?: string;
  leftIcon?: string;
  onClickHelperText?: VoidFunction;
  actionButtonTitle?: string;
  additionalButtonTitle?: string;
  chartToggleButton?: string;
  dropDownSelect?: string;
  dropDownOptions?: string[];
  dropDownLabel?: string;
  additionalExportButtonTitle?: string;
  additionalExportPdfButtonTitle?: string;
  onClickDropDownOption?: (option: string) => void;
  onClickChartToggleButton?: VoidFunction;
  onClickActionButton?: VoidFunction;
  onClickAdditionalButton?: VoidFunction;
  searchBarProps?: SearchBarProps;
  onClose?: () => void;
  variant?: 'list' | 'dialog';
  dropDownDisabled?: boolean;
  titleStyle?: React.CSSProperties;
  onFilterClick?: (event: React.MouseEvent<HTMLElement>) => void;
  rightIcon?: string;
  onClickRightIcon?: VoidFunction;
  tooltip?: string;
  orientation?: string;
  exportedData?: any;
}

export const Header = ({
  exportedData,
  goBack,
  title,
  helperText,
  leftIcon,
  onClickHelperText,
  actionButtonTitle,
  additionalButtonTitle,
  dropDownSelect,
  dropDownOptions,
  dropDownLabel,
  additionalExportButtonTitle,
  additionalExportPdfButtonTitle,
  onClickDropDownOption,
  onClickActionButton,
  onClickAdditionalButton,
  searchBarProps,
  onClose,
  dropDownDisabled,
  titleStyle,
  onFilterClick,
  variant = 'list',
  rightIcon,
  onClickRightIcon,
  orientation,
  tooltip,
}: Props) => {
  const [anchorEl, setAnchorEl] = useState<{
    anchor: null | HTMLElement;
    exportedTo?: string;
  }>({
    anchor: null,
    exportedTo: '',
  });

  const open = Boolean(anchorEl.anchor);
  const fieldToExportDialog = useDialog();

  // Replaces <Hidden smDown>
  const isSmallScreen = useMediaQuery((theme: any) => theme.breakpoints.down('sm'));

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl({ anchor: event.currentTarget });
  };

  const handleClose = () => {
    setAnchorEl({ anchor: null });
  };

  const handleExportClick = (value: string) => {
    setAnchorEl({ ...anchorEl, exportedTo: value });
    fieldToExportDialog.show();
  };

  return (
    <Stack sx={{ p: 2, width: '100%' }}>
      <StyledRowSpace>
        {leftIcon && (
          <SvgColor
            src={`/assets/icons/card/${leftIcon}.svg`}
            sx={{
              width: '25px',
              height: '25px',
              mr: 1,
              color: (theme) => theme.palette.taskboard.raisinBlack,
            }}
          />
        )}

        {goBack && (
          <IconButton
            aria-label="back"
            sx={{
              color: (theme) => theme.palette.common.black,
            }}
            onClick={goBack}
          >
            <Iconify icon="fluent:ios-arrow-24-filled" width={20} fontWeight={900} />
          </IconButton>
        )}

        {variant === 'dialog' ? (
          <Typography variant="h6" sx={titleStyle}>
            {title}
          </Typography>
        ) : (
          !isSmallScreen && (
            <Typography variant="h5" sx={titleStyle}>
              {title}
            </Typography>
          )
        )}

        <Stack flexDirection="row" flex={1} justifyContent="flex-end">
          {Boolean(additionalButtonTitle) && (
            <Button
              variant="outlined"
              onClick={onClickAdditionalButton}
              color="inherit"
              sx={{ mr: 2 }}
            >
              {additionalButtonTitle}
            </Button>
          )}

          {dropDownOptions && dropDownOptions.length > 0 && (
            <FormControl variant="outlined">
              <InputLabel
                sx={{
                  color: (theme) => theme.palette.grey[500],
                  '&.Mui-focused': {
                    color: (theme) => theme.palette.grey[500],
                  },
                }}
              >
                {dropDownLabel}
              </InputLabel>

              <Select
                variant="outlined"
                value={dropDownSelect}
                label={dropDownLabel}
                disabled={dropDownDisabled}
                onChange={(event: { target: { value: string } }) => {
                  onClickDropDownOption?.(event.target.value);
                }}
                sx={{
                  height: '6vh',
                  mr: 2,
                  fontSize: '14px',
                  fontWeight: 700,
                }}
              >
                {dropDownOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {Boolean(actionButtonTitle) && (
            <Button variant="contained" onClick={onClickActionButton}>
              {actionButtonTitle}
            </Button>
          )}

          {rightIcon && (
            <Tooltip title={tooltip}>
              <SvgColor
                src={`/assets/icons/card/${rightIcon}.svg`}
                sx={{
                  width: '25px',
                  height: '25px',
                  mr: 1,
                  mt: 0.5,
                  cursor: 'pointer',
                }}
                onClick={onClickRightIcon}
              />
            </Tooltip>
          )}
        </Stack>

        {onClose && (
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Iconify icon="material-symbols:close" />
          </IconButton>
        )}

        {Boolean(additionalExportButtonTitle) && (
          <Button
            id="basic-button"
            aria-controls={open ? 'basic-menu' : undefined}
            aria-haspopup="true"
            variant="contained"
            aria-expanded={open ? 'true' : undefined}
            onClick={handleClick}
          >
            Export
          </Button>
        )}

        <Menu
          id="basic-menu"
          anchorEl={anchorEl.anchor}
          open={open}
          onClose={handleClose}
          MenuListProps={{
            'aria-labelledby': 'basic-button',
          }}
        >
          <MenuItem
            onClick={() => {
              handleExportClick('csv');
            }}
            sx={{ fontSize: 13 }}
          >
            {Strings.button.exportCsv}
          </MenuItem>

          <MenuItem
            onClick={() => {
              handleExportClick('pdf');
            }}
            sx={{ fontSize: 13 }}
          >
            {Strings.button.exportpdf}
          </MenuItem>
        </Menu>
      </StyledRowSpace>

      {Boolean(helperText) && <Box sx={{ mt: 3 }} />}

      {Boolean(helperText) && (
        <Alert
          severity="info"
          sx={{
            bgcolor: (theme) => alpha(theme.palette.info.main, 0.16),
          }}
        >
          <Button variant="text" sx={{ padding: 0 }} onClick={onClickHelperText}>
            <Link variant="subtitle2" underline="always">
              {helperText}
            </Link>
          </Button>
        </Alert>
      )}

      {Boolean(searchBarProps) && (
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 3 }}>
          <Box flexGrow={1}>
            <SearchBar {...searchBarProps} fullWidth />
          </Box>

          {onFilterClick && (
            <IconButton
              aria-label="filter"
              onClick={onFilterClick}
              sx={{
                color: (theme) => theme.palette.common.black,
              }}
            >
              <Iconify icon="mdi:filter-variant" width={24} />
            </IconButton>
          )}
        </Stack>
      )}

      {fieldToExportDialog.visible && (
        <FieldToExportDialog
          exportDialogOpen
          handleClose={fieldToExportDialog.hide}
          exportedData={{
            ...exportedData,
            exportedTo: anchorEl?.exportedTo,
          }}
        />
      )}
    </Stack>
  );
};
